import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, DatePicker, Dialog, Field, FieldLabel, Select, Typography } from "@akuna/ui";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { useStayPlans } from "@/hooks/useStayPlans";
import type { StayPlanKind, StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { getBillingCycle } from "@/lib/daycare-hotel/stayPlans";
import { fixedDayDatesInRange, snapToFixedDay } from "@/lib/daycare-hotel/planFixedDays";
import { formatDateBR } from "@/lib/date-utils";
import type { StayPlanContractDraft } from "./stayPlanContractDraft";
import { FixedWeekdaysField } from "./FixedWeekdaysField";
import { PackageDatesField } from "./PackageDatesField";

export interface StayPlanContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: StayPlanModule | null;
  availableModules: StayPlanModule[];
  defaultStartsOn: string;
  submitting?: boolean;
  kindFilter?: StayPlanKind;
  onSubmit: (draft: StayPlanContractDraft) => void;
}

export function StayPlanContractDialog({
  open,
  onOpenChange,
  module,
  availableModules,
  defaultStartsOn,
  submitting = false,
  kindFilter,
  onSubmit,
}: StayPlanContractDialogProps) {
  const { t } = useTranslation("daycareHotel");
  const [selectedModule, setSelectedModule] = useState<StayPlanModule | null>(
    module ?? (availableModules.length === 1 ? availableModules[0] : null),
  );
  const plans = useStayPlans(selectedModule);
  const options = useMemo(() => (plans.data ?? []).filter((plan) => plan.isActive && (!kindFilter || plan.kind === kindFilter)), [kindFilter, plans.data]);
  const [planId, setPlanId] = useState<string | null>(null);
  const [planSearch, setPlanSearch] = useState("");
  const [requestedStart, setRequestedStart] = useState(defaultStartsOn);
  const [billingTiming, setBillingTiming] = useState<"start" | "end">("start");
  const [fixedWeekdays, setFixedWeekdays] = useState<number[]>([]);
  const [packageDates, setPackageDates] = useState<string[]>([]);

  useEffect(() => {
    setRequestedStart(defaultStartsOn);
  }, [defaultStartsOn]);

  useEffect(() => {
    setSelectedModule(module ?? (availableModules.length === 1 ? availableModules[0] : null));
    setPlanId(null);
    setPlanSearch("");
  }, [availableModules, kindFilter, module]);

  useEffect(() => {
    setPlanSearch(options.find((plan) => plan.id === planId)?.name ?? "");
  }, [options, planId]);

  useEffect(() => {
    setFixedWeekdays([]);
    setPackageDates([]);
  }, [planId, selectedModule]);

  const selected = options.find((plan) => plan.id === planId) ?? null;
  const autocompleteOptions: AutocompleteOption[] = options.map((plan) => ({ value: plan.id, label: plan.name }));
  const moduleOptions = availableModules.map((availableModule) => ({
    value: availableModule,
    label: t(`planContract.moduleOptions.${availableModule}`),
  }));
  // Só matrícula de creche amarra dias fixos; pacote e demais regras não.
  const needsFixedDays =
    selectedModule === "daycare" && selected?.kind === "recurring" && selected?.usageRule === "per_week";
  const requiredDays = needsFixedDays ? selected?.usageLimit ?? 0 : 0;
  const fixedDaysReady = !needsFixedDays || fixedWeekdays.length === requiredDays;
  // A primeira diária do plano é o início: começar numa quinta com Seg/Qua
  // faria o ciclo nascer num dia em que o pet não vem.
  const startsOn = needsFixedDays ? snapToFixedDay(requestedStart, fixedWeekdays) : requestedStart;
  const startWasSnapped = startsOn !== requestedStart;
  // O mês tem 4 ou 5 ocorrências conforme o calendário; mostrar o número antes
  // de confirmar evita a surpresa de uma diária a mais na primeira fatura.
  const previewDates = useMemo(() => {
    if (!needsFixedDays || fixedWeekdays.length === 0) return [];
    const cycle = getBillingCycle(startsOn, startsOn);
    return fixedDayDatesInRange({ from: startsOn, to: cycle.end, weekdays: fixedWeekdays, weekInterval: selected?.weekInterval ?? 1, startsOn });
  }, [needsFixedDays, fixedWeekdays, startsOn, selected?.weekInterval]);
  const isPackage = selected?.kind === "day_package";
  // Cobrar no final exige um dia final: sem data escolhida não há o que vencer.
  const packageDatesReady = !isPackage || billingTiming === "start" || packageDates.length > 0;
  const canSubmit = Boolean(selectedModule && selected) && fixedDaysReady && packageDatesReady && !submitting;

  const submit = (payNow: boolean) => {
    if (!selected) return;
    if (!selectedModule) return;
    onSubmit({
      module: selectedModule,
      planId: selected.id,
      startsOn,
      billingTiming,
      payNow,
      ...(needsFixedDays ? { fixedWeekdays } : {}),
      ...(isPackage ? { packageDates } : {}),
    });
  };

  return (
    <Dialog
      className="akuna-dialog--wide akuna-dialog--compact"
      closeLabel={t("common.cancel")}
      open={open}
      onOpenChange={onOpenChange}
      title={kindFilter === "day_package" ? t("planContract.titlePackage") : kindFilter === "recurring" ? t("planContract.titlePlan") : t("planContract.title")}
      mode="adaptive"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => submit(billingTiming === "start")}>
            {billingTiming === "start" ? t("planContract.saveAndPay") : t("planContract.save")}
          </Button>
        </div>
      }
      >
      <div className="grid gap-4">
        <div className={availableModules.length > 1 ? "grid gap-4 sm:grid-cols-2" : undefined}>
          {availableModules.length > 1 ? (
            <Select
              label={t("planContract.module")}
              options={moduleOptions}
              placeholder={t("planContract.module")}
              value={selectedModule ?? ""}
              onValueChange={(value) => {
                setSelectedModule(value as StayPlanModule);
                setPlanId(null);
                setPlanSearch("");
              }}
            />
          ) : null}
          <Field>
            <FieldLabel>{t("planContract.plan")}</FieldLabel>
          <Autocomplete
            options={autocompleteOptions}
            value={planSearch}
            onChange={(value) => {
              setPlanSearch(value);
              if (!options.some((plan) => plan.name === value)) setPlanId(null);
            }}
            onSelect={(option) => {
              setPlanId(option.value);
              setPlanSearch(option.label);
            }}
            selectedValue={planId ?? ""}
            ariaLabel={t("planContract.plan")}
            placeholder={t("planContract.plan")}
            emptyMessage={t("planContract.noPlans")}
            popoverPortalled={false}
            disabled={!selectedModule}
          />
          </Field>
        </div>

        <DatePicker label={t("planContract.startsOn")} value={startsOn} onDateChange={setRequestedStart} />
        {startWasSnapped ? (
          <p role="note" className="-mt-2 text-xs text-muted-foreground">
            {t("planContract.startSnapped", { date: formatDateBR(startsOn) })}
          </p>
        ) : null}

        {needsFixedDays ? (
          <Field>
            <FieldLabel>{t("fixedDays.fieldLabel")}</FieldLabel>
            <FixedWeekdaysField
              value={fixedWeekdays}
              required={requiredDays}
              onChange={setFixedWeekdays}
              disabled={submitting}
              hideStatus
            />
            {previewDates.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5">
                {previewDates.slice(0, 6).map((date) => (
                  <li key={date}>
                    <span className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {formatDateBR(date)}
                    </span>
                  </li>
                ))}
                {previewDates.length > 6 ? (
                  <li>
                    <span className="inline-flex items-center rounded-md border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground">
                      {t("fixedDays.previewMore")}
                    </span>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </Field>
        ) : null}

        {isPackage && selected ? (
          <Field>
            <FieldLabel>{t("packageDates.sectionLabel")}</FieldLabel>
            <PackageDatesField
              module={selectedModule ?? "daycare"}
              includedDays={selected.includedDays ?? 0}
              value={packageDates}
              onChange={setPackageDates}
              disabled={submitting}
            />
            {!packageDatesReady ? (
              <p className="text-xs text-destructive">{t("packageDates.requiredForEndBilling")}</p>
            ) : null}
          </Field>
        ) : null}

        <Field>
          <FieldLabel>{t("planContract.billingTiming")}</FieldLabel>
          <div role="radiogroup" aria-label={t("planContract.billingTiming")} className="grid gap-2 sm:grid-cols-2">
            {(["start", "end"] as const).map((timing) => (
              <button
                key={timing}
                type="button"
                role="radio"
                aria-checked={billingTiming === timing}
                onClick={() => setBillingTiming(timing)}
                className={`rounded-xl border px-3 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${billingTiming === timing ? "border-primary bg-primary/10" : "border-border bg-card"}`}
              >
                {/* No hotel o ciclo é a estadia, não o mês: os rótulos mudam. */}
                <span className="block text-sm font-semibold text-foreground">
                  {selectedModule === "hotel"
                    ? t(timing === "end" ? "hotelBilling.check_out" : "hotelBilling.check_in")
                    : t(`planContract.billingTimingOptions.${timing}.title`)}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {selectedModule === "hotel"
                    ? t(timing === "end" ? "planContract.hotelBillingEndHint" : "planContract.hotelBillingStartHint")
                    : t(`planContract.billingTimingOptions.${timing}.description`)}
                </span>
              </button>
            ))}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}
